// UnifiedDetailedNetWorkingCapital.tsx
// Frontend-only NWC calculations with view-based CCC metrics
import * as React from 'react';
import { useState, useEffect, forwardRef } from 'react';
import { ChevronDown, ChevronRight, ChevronDownSquare, ChevronUpSquare } from 'lucide-react';
import { AccountEntry, AccountType, CategoryTemplate, Project, ViewType } from '../../types';
import { ViewToggle } from '../ui/ViewToggle';
import { compareMonthYear } from '../../lib/financial/calculations';
import { isDefinitionalAdjustment, isTradeWorkingCapital } from '../../lib/financial/nwcUtils';
import { getDateForPeriod } from '../../lib/utils/dateMapping';
import { FrontendNWCCalculationService } from '../../lib/financial/frontendNWCCalculations';

interface UnifiedDetailedNetWorkingCapitalProps {
  accounts: AccountEntry[];
  activeTemplate: CategoryTemplate | null;
  selectedProject?: Project | null; // Add this
  netRevenueTimeSeries?: Record<string, number>; // NEW PROP
}

// ViewType now imported from types.ts

interface NWCSection {
  id: string;
  title: string;
  types: AccountType[];
}

// Interface for defining special NWC role categories
interface NWCRoleMapping {
  role: 'cash' | 'short-term-debt' | 'inventory' | 'accounts-receivable' | 'accounts-payable';
  categoryValues: string[];
  displayName: string;
  description?: string;
}

// NWC Role Legend component
const NWCRoleLegend = () => {
  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border mb-6">
      <h3 className="font-medium text-gray-900 mb-2">Working Capital Categories</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <h4 className="font-medium text-sm text-gray-700">Definitional Adjustments</h4>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
              Cash
            </span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
              Short-term Debt
            </span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
              Debt-Like Items
            </span>
          </div>
          <p className="text-xs text-gray-500">
            These categories are excluded from Definitional Net Working Capital
          </p>
        </div>
        
        <div className="space-y-2">
          <h4 className="font-medium text-sm text-gray-700">Trade Working Capital</h4>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Accounts Receivable
            </span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Inventory
            </span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Accounts Payable
            </span>
          </div>
          <p className="text-xs text-gray-500">
            These categories are included in the Days calculations (DIO/DSO/DPO) at the bottom of the table 
          </p>
        </div>
      </div>
    </div>
  );
};

// Update the component declaration to use forwardRef (replace the existing export line)
export const UnifiedDetailedNetWorkingCapital = forwardRef<void, UnifiedDetailedNetWorkingCapitalProps>(({ 
  accounts, 
  activeTemplate, 
  selectedProject,
  netRevenueTimeSeries
}, ref) => {
  // Initialize viewType based on project period type if available
  const getInitialViewType = (): ViewType => {
    if (selectedProject?.period_type === 'annual') return 'yearly';
    if (selectedProject?.period_type === 'quarterly') return 'quarterly';
    return 'monthly';
  };

  const [viewType, setViewType] = useState<ViewType>(getInitialViewType());
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // Frontend calculation - always use frontend calculations only
  const currentDataSource = React.useMemo(() => {
    if (!activeTemplate || accounts.length === 0) return null;
    return FrontendNWCCalculationService.calculate(accounts, activeTemplate, 'monthly', netRevenueTimeSeries);
  }, [accounts, activeTemplate, netRevenueTimeSeries]);

  // Define sections using account types
  const sections: NWCSection[] = [
    {
      id: 'current-assets',
      title: 'Current Assets',
      types: ['current-asset', 'non-current-asset']
    },
    {
      id: 'current-liabilities',
      title: 'Current Liabilities',
      types: ['current-liability', 'non-current-liability']
    }
  ];

  // Define special NWC role categories with default mappings
  // These will be overridden by template mappings if available
  const defaultNWCRoleMappings: NWCRoleMapping[] = [
    {
      role: 'cash',
      categoryValues: ['cash', 'cash-and-cash-equivalents'],
      displayName: 'Cash',
      description: 'Cash and Cash Equivalents'
    },
    {
      role: 'short-term-debt',
      categoryValues: ['short-term-debt', 'short-term-borrowings'],
      displayName: 'Short-term Debt',
      description: 'Short-term Borrowings'
    },
    {
      role: 'inventory',
      categoryValues: ['inventory'],
      displayName: 'Inventory',
      description: 'Inventory'
    },
    {
      role: 'accounts-receivable',
      categoryValues: ['trade-accounts-receivable', 'accounts-receivable'],
      displayName: 'Trade Accounts Receivable',
      description: 'AR / Net Revenue * days'
    },
    {
      role: 'accounts-payable',
      categoryValues: ['trade-accounts-payable', 'accounts-payable'],
      displayName: 'Trade Accounts Payable',
      description: 'AP / COGS * days'
    }
  ];

  // Get all months and sort them chronologically
  const allMonths = [...new Set(accounts.map(a => a.month))]
    .sort(compareMonthYear);

  // Function to determine if a month is a quarter end
  const isQuarterEnd = (month: string) => {
    const monthStr = month.split('-')[0];
    return ['Mar', 'Jun', 'Sep', 'Dec'].includes(monthStr);
  };

  // Function to determine if a month is December
  const isDecember = (month: string) => {
    return month.split('-')[0] === 'Dec';
  };

  // Function to get actual days in a period - period-aware
  const getPeriodDays = (endMonth: string) => {
    if (viewType === 'monthly') {
      return getDaysInMonth(endMonth);
    } else if (viewType === 'quarterly') {
      // For quarterly, determine the quarter from the month and calculate quarter days
      const [monthStr, yearStr] = endMonth.split('-');
      const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(monthStr);
      const quarterNum = Math.floor(monthIndex / 3) + 1;
      const quarterStr = `Q${quarterNum}'${yearStr}`;
      
      // Calculate days for each month in the quarter
      const quarterMonths = getMonthsInQuarter(quarterNum, 2000 + parseInt(yearStr));
      return quarterMonths.reduce((quarterDays, month) => {
        return quarterDays + new Date(2000 + parseInt(yearStr), month + 1, 0).getDate();
      }, 0);
    } else { // yearly
      // For annual, use the full year
      const [, yearStr] = endMonth.split('-');
      const year = 2000 + parseInt(yearStr);
      // Check if leap year
      const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
      return isLeapYear ? 366 : 365;
    }
  };

  // Helper function to get month indices for a given quarter
  const getMonthsInQuarter = (quarterNum: number, year: number): number[] => {
    switch (quarterNum) {
      case 1: return [0, 1, 2]; // Jan, Feb, Mar
      case 2: return [3, 4, 5]; // Apr, May, Jun
      case 3: return [6, 7, 8]; // Jul, Aug, Sep
      case 4: return [9, 10, 11]; // Oct, Nov, Dec
      default: return [];
    }
  };
  
  // Get displayed periods based on view type with proper sorting
  const getDisplayedPeriods = () => {
    const mostRecentMonth = allMonths[allMonths.length - 1];

    if (viewType === 'monthly') {
      return allMonths;
    } else if (viewType === 'quarterly') {
      const quarterEndMonths = allMonths.filter(isQuarterEnd);
      if (!isQuarterEnd(mostRecentMonth)) {
        quarterEndMonths.push(mostRecentMonth);
      }
      return quarterEndMonths.sort(compareMonthYear);
    } else {
      const decemberMonths = allMonths.filter(isDecember);
      if (!isDecember(mostRecentMonth)) {
        decemberMonths.push(mostRecentMonth);
      }
      return decemberMonths.sort(compareMonthYear);
    }
  };

  const months = getDisplayedPeriods();

  // Get months in a period function
  const getMonthsInPeriod = (endMonth: string) => {
    if (viewType === 'monthly') {
      return [endMonth];
    }

    const [endMonthStr, yearStr] = endMonth.split('-');
    const year = parseInt(yearStr);
    const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
                       .indexOf(endMonthStr);

    let startMonthIndex;
    if (viewType === 'quarterly') {
      startMonthIndex = Math.floor(monthIndex / 3) * 3; // Quarter start (0, 3, 6, or 9)
    } else { // yearly
      startMonthIndex = 0;
    }

    const result = allMonths.filter(month => {
      const [monthStr, monthYearStr] = month.split('-');
      const monthYear = parseInt(monthYearStr);
      const monthIdx = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
                       .indexOf(monthStr);

      if (monthYear !== year) return false;
      return monthIdx >= startMonthIndex && monthIdx <= monthIndex;
    });

    return result;
  };
  
  // Calculate average period balance
  const calculateAveragePeriodBalance = (category: string, endMonth: string) => {
    const monthsInPeriod = getMonthsInPeriod(endMonth);
    const totalBalance = monthsInPeriod.reduce((total, month) => {
      return total + calculateMonthlyTotal(category, month);
    }, 0);
    return totalBalance / monthsInPeriod.length;
  };
  
  // Calculate period total
  const calculatePeriodTotal = (category: string, endMonth: string) => {
    const monthsInPeriod = getMonthsInPeriod(endMonth);
    return monthsInPeriod.reduce((total, month) => {
      return total + calculateMonthlyTotal(category, month);
    }, 0);
  };

  const toggleCategory = (category: string) => {
    setExpanded(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const expandAll = () => {
    if (!currentDataSource) return;
    const newExpanded: Record<string, boolean> = {};
    currentDataSource.line_items.forEach((item: any) => {
      if (item.type === 'category_header') {
        newExpanded[item.category_value] = true;
      }
    });
    setExpanded(newExpanded);
  };

  const collapseAll = () => {
    setExpanded({});
  };

  const getNWCRole = (category: string) => {
    if (!activeTemplate) return null;
    
    const templateCategory = activeTemplate.categories.find(cat => cat.value === category);
    return templateCategory?.nwcRole || null;
  };
  
  // Get categories by type from active template
  const getCategoriesByType = (types: AccountType[]): string[] => {
    if (!activeTemplate) {
      return [];
    }

    return activeTemplate.categories
      .filter(cat => types.includes(cat.type))
      .map(cat => cat.value);
  };

  // Get definitional adjustment categories
  const getDefinitionalAdjustmentCategories = (): string[] => {
    if (!activeTemplate) return [];
    
    return activeTemplate.categories
      .filter(cat => isDefinitionalAdjustment(cat.nwcRole))
      .map(cat => cat.value);
  };

  // Get trade working capital categories
  const getTradeWorkingCapitalCategories = (): string[] => {
    if (!activeTemplate) return [];
    
    return activeTemplate.categories
      .filter(cat => isTradeWorkingCapital(cat.nwcRole))
      .map(cat => cat.value);
  };

  // Calculate total for definitional adjustments
  const calculateDefinitionalAdjustments = (month: string): number => {
    const adjustmentCategories = getDefinitionalAdjustmentCategories();
    return adjustmentCategories.reduce((total, category) => {
      const role = getNWCRole(category);
      if (!role) return total;
      // For cash, subtract from NWC (negative adjustment)
      if (role === 'cash') {
        return total - calculateMonthlyTotal(category, month);
      }
      // For debt or debt-like, add to NWC (positive adjustment)
      if (role === 'short-term-debt' || role === 'debt-like') {
        return total + calculateMonthlyTotal(category, month);
      }
      return total;
    }, 0);
  };

  // Calculate trade working capital
  const calculateTradeWorkingCapital = (month: string): number => {
    const assetCategories = getTradeWorkingCapitalCategories()
      .filter(category => {
        const templateCategory = activeTemplate?.categories.find(cat => cat.value === category);
        return templateCategory?.type === 'current-asset';
      });
    const liabilityCategories = getTradeWorkingCapitalCategories()
      .filter(category => {
        const templateCategory = activeTemplate?.categories.find(cat => cat.value === category);
        return templateCategory?.type === 'current-liability';
      });
    const assetTotal = assetCategories.reduce((total, category) => {
      return total + calculateMonthlyTotal(category, month);
    }, 0);
    const liabilityTotal = liabilityCategories.reduce((total, category) => {
      return total + calculateMonthlyTotal(category, month);
    }, 0);
    return assetTotal - liabilityTotal;
  };

  // Get the category display name from template
  const getCategoryDisplayName = (categoryValue: string): string => {
    if (!activeTemplate) return categoryValue;
    
    const templateCategory = activeTemplate.categories.find(cat => cat.value === categoryValue);
    return templateCategory?.label || categoryValue;
  };

  // Get category type based on active template
  const getCategoryType = (category: string): AccountType => {
    if (!activeTemplate) return 'n/a';
    
    const templateCategory = activeTemplate.categories.find(cat => cat.value === category);
    return templateCategory?.type || 'n/a';
  };

  // Get accounts for a specific category and month
  const getCategoryAccountsByMonth = (category: string, month: string) => {
    return accounts.filter(account => account.category === category && account.month === month);
  };

  // Calculate total for a category in a specific month
  const calculateMonthlyTotal = (category: string, month: string) => {
    const monthlyAccounts = getCategoryAccountsByMonth(category, month);
    const categoryType = getCategoryType(category);
    const isAsset = ['current-asset', 'non-current-asset'].includes(categoryType);
    const isContraAsset = ['contra-current-asset', 'contra-non-current-asset'].includes(categoryType);
    
    return monthlyAccounts.reduce((total, account) => {
      return total + ((isAsset || isContraAsset) 
        ? account.debit - account.credit 
        : account.credit - account.debit);
    }, 0);
  };

  // Calculate total for a section in a specific month
  const calculateSectionTotal = (types: AccountType[], month: string) => {
    const categories = getCategoriesByType(types);
    return categories.reduce((total, category) => {
      return total + calculateMonthlyTotal(category, month);
    }, 0);
  };

  
  // Get categories and totals for NWC role mapping
  const getNWCRoleTotal = (role: NWCRoleMapping | undefined, month: string) => {
    if (!role) return 0;
    // First check if categories have the NWC role tag in the template
    if (activeTemplate) {
      // Find categories in template with matching NWC role
      const matchingCategories = activeTemplate.categories
        .filter(cat => cat.nwcRole === role.role)
        .map(cat => cat.value);
      // If found categories with matching roles, use those
      if (matchingCategories.length > 0) {
        return matchingCategories.reduce((total, category) => {
          return total + calculateMonthlyTotal(category, month);
        }, 0);
      }
    }
    // Fallback to the original behavior if no NWC roles found in template
    const validCategories = role.categoryValues.filter(catValue => 
      accounts.some(account => account.category === catValue)
    );
    if (validCategories.length === 0) {
      return 0; // No matching categories found
    }
    return validCategories.reduce((total, category) => {
      return total + calculateMonthlyTotal(category, month);
    }, 0);
  };

  // Calculate Net Working Capital
  const calculateNetWorkingCapital = (month: string) => {
    const totalCurrentAssets = calculateSectionTotal(['current-asset', 'non-current-asset'], month);
    const totalCurrentLiabilities = calculateSectionTotal(['current-liability', 'non-current-liability'], month);
    return totalCurrentAssets - totalCurrentLiabilities;
  };

  // Calculate Adjusted Working Capital
  const calculateAdjustedWorkingCapital = (month: string) => {
    return calculateNetWorkingCapital(month) + calculateDefinitionalAdjustments(month);
  };

  // Helper to get Net Revenue total for a period (for quarterly/yearly), matching DSO logic
  const getNetRevenueTotal = (monthsInPeriod: string[]) => {
    if (!activeTemplate) return 0;
    const revenueCategories = activeTemplate.categories
      .filter(cat => cat.type === 'revenue' || cat.type === 'contra-revenue')
      .map(cat => cat.value);
    return monthsInPeriod.reduce((sum, month) => {
      return sum + revenueCategories.reduce((t, category) => t + calculateMonthlyTotal(category, month), 0);
    }, 0);
  };

  // Updated: Calculate Working Capital % of Net Revenue for all views
  const calculateWorkingCapitalPercentage = (month: string) => {
    if (viewType === 'monthly') {
      const workingCapital = calculateAdjustedWorkingCapital(month);
      const netRevenue = netRevenueTimeSeries ? (netRevenueTimeSeries[month] || 0) : 0;
      if (netRevenue === 0) return 0;
      const daysInMonth = getDaysInMonth(month);
      return (workingCapital / netRevenue) * daysInMonth / 365 * 100;
    } else {
      const monthsInPeriod = getMonthsInPeriod(month);
      const totalWorkingCapital = monthsInPeriod.reduce((total, m) => {
        return total + calculateAdjustedWorkingCapital(m);
      }, 0);
      const avgWorkingCapital = totalWorkingCapital / monthsInPeriod.length;
      const periodNetRevenue = getNetRevenueTotal(monthsInPeriod); // Use local sum, not backend
      if (periodNetRevenue === 0) return 0;
      const actualDays = getPeriodDays(month);
      return (avgWorkingCapital / periodNetRevenue) * actualDays / 365 * 100;
    }
  };

  // Function to get actual days in a month
  const getDaysInMonth = (monthStr: string) => {
    const [month, year] = monthStr.split('-');
    const monthMap: { [key: string]: number } = {
      'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
      'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
    };
    const fullYear = 2000 + parseInt(year);
    return new Date(fullYear, monthMap[month] + 1, 0).getDate();
  };

  // Working Capital Metrics calculations with NWC role mappings
  // Updated to match FinancialRatios.tsx calculation conventions based on viewType and period_type
  const calculateDIO = (month: string) => {
    const inventoryRole = defaultNWCRoleMappings.find(mapping => mapping.role === 'inventory');

    // Find COGS categories from template
    const cogsCategories = activeTemplate
      ? activeTemplate.categories
          .filter(cat => cat.type === 'cogs' || cat.type === 'contra-cogs')
          .map(cat => cat.value)
      : ['cost-of-goods-sold'];

    const periodType = selectedProject?.period_type || 'monthly';

    // Monthly view: Use single period (month/quarter/year) - no averaging
    if (viewType === 'monthly') {
      const inventory = inventoryRole ? getNWCRoleTotal(inventoryRole, month) : 0;
      const cogs = cogsCategories.reduce((total, category) => {
        return total + Math.abs(calculateMonthlyTotal(category, month));
      }, 0);
      const daysInMonth = getDaysInMonth(month);
      return cogs !== 0 ? (inventory / cogs) * daysInMonth : 0;
    }

    // Quarterly view with monthly backend data: Use L3MA-style (3 months)
    if (viewType === 'quarterly' && periodType === 'monthly') {
      const monthsArr: string[] = getMonthsInPeriod(month);
      const avgInventory = inventoryRole
        ? monthsArr.reduce((sum, m: string) => sum + getNWCRoleTotal(inventoryRole, m), 0) / monthsArr.length
        : 0;
      const totalCOGS = cogsCategories.reduce((total, category) => {
        return total + Math.abs(calculatePeriodTotal(category, month));
      }, 0);
      const actualDays = getPeriodDays(month);
      return totalCOGS !== 0 ? (avgInventory / totalCOGS) * actualDays : 0;
    }

    // Quarterly view with quarterly backend data: Use LQA-style (spot values)
    if (viewType === 'quarterly' && periodType === 'quarterly') {
      const inventory = inventoryRole ? getNWCRoleTotal(inventoryRole, month) : 0;
      const cogs = cogsCategories.reduce((total, category) => {
        return total + Math.abs(calculateMonthlyTotal(category, month));
      }, 0);
      const actualDays = getPeriodDays(month);
      return cogs !== 0 ? (inventory / cogs) * actualDays : 0;
    }

    // Yearly view with monthly backend data: Use LTM-style (12 months)
    if (viewType === 'yearly' && periodType === 'monthly') {
      const monthsArr: string[] = getMonthsInPeriod(month);
      const avgInventory = inventoryRole
        ? monthsArr.reduce((sum, m: string) => sum + getNWCRoleTotal(inventoryRole, m), 0) / monthsArr.length
        : 0;
      const totalCOGS = cogsCategories.reduce((total, category) => {
        return total + Math.abs(calculatePeriodTotal(category, month));
      }, 0);
      const actualDays = getPeriodDays(month);
      return totalCOGS !== 0 ? (avgInventory / totalCOGS) * actualDays : 0;
    }

    // Yearly view with quarterly backend data: Use 4 quarters
    if (viewType === 'yearly' && periodType === 'quarterly') {
      const monthsArr: string[] = getMonthsInPeriod(month);
      const avgInventory = inventoryRole
        ? monthsArr.reduce((sum, m: string) => sum + getNWCRoleTotal(inventoryRole, m), 0) / monthsArr.length
        : 0;
      const totalCOGS = cogsCategories.reduce((total, category) => {
        return total + Math.abs(calculatePeriodTotal(category, month));
      }, 0);
      const actualDays = getPeriodDays(month);
      return totalCOGS !== 0 ? (avgInventory / totalCOGS) * actualDays : 0;
    }

    // Yearly view with annual backend data: Use single year
    if (viewType === 'yearly' && periodType === 'annual') {
      const inventory = inventoryRole ? getNWCRoleTotal(inventoryRole, month) : 0;
      const cogs = cogsCategories.reduce((total, category) => {
        return total + Math.abs(calculateMonthlyTotal(category, month));
      }, 0);
      const actualDays = getPeriodDays(month);
      return cogs !== 0 ? (inventory / cogs) * actualDays : 0;
    }

    return 0;
  };

  const calculateDSO = (month: string) => {
    const arRole = defaultNWCRoleMappings.find(mapping => mapping.role === 'accounts-receivable');

    // Find revenue categories from template
    const revenueCategories = activeTemplate
      ? activeTemplate.categories
          .filter(cat => cat.type === 'revenue' || cat.type === 'contra-revenue')
          .map(cat => cat.value)
      : [];

    const periodType = selectedProject?.period_type || 'monthly';

    // Monthly view: Use single period (month/quarter/year) - no averaging
    if (viewType === 'monthly') {
      const accountsReceivable = arRole ? getNWCRoleTotal(arRole, month) : 0;
      const revenue = revenueCategories.reduce((total, category) => {
        return total + Math.abs(calculateMonthlyTotal(category, month));
      }, 0);
      const daysInMonth = getDaysInMonth(month);
      return revenue !== 0 ? (accountsReceivable / revenue) * daysInMonth : 0;
    }

    // Quarterly view with monthly backend data: Use L3MA-style (3 months)
    if (viewType === 'quarterly' && periodType === 'monthly') {
      const monthsArr: string[] = getMonthsInPeriod(month);
      const avgAR = arRole
        ? monthsArr.reduce((sum, m: string) => sum + getNWCRoleTotal(arRole, m), 0) / monthsArr.length
        : 0;
      const totalRevenue = revenueCategories.reduce((total, category) => {
        return total + Math.abs(calculatePeriodTotal(category, month));
      }, 0);
      const actualDays = getPeriodDays(month);
      return totalRevenue !== 0 ? (avgAR / totalRevenue) * actualDays : 0;
    }

    // Quarterly view with quarterly backend data: Use LQA-style (spot values)
    if (viewType === 'quarterly' && periodType === 'quarterly') {
      const accountsReceivable = arRole ? getNWCRoleTotal(arRole, month) : 0;
      const revenue = revenueCategories.reduce((total, category) => {
        return total + Math.abs(calculateMonthlyTotal(category, month));
      }, 0);
      const actualDays = getPeriodDays(month);
      return revenue !== 0 ? (accountsReceivable / revenue) * actualDays : 0;
    }

    // Yearly view with monthly backend data: Use LTM-style (12 months)
    if (viewType === 'yearly' && periodType === 'monthly') {
      const monthsArr: string[] = getMonthsInPeriod(month);
      const avgAR = arRole
        ? monthsArr.reduce((sum, m: string) => sum + getNWCRoleTotal(arRole, m), 0) / monthsArr.length
        : 0;
      const totalRevenue = revenueCategories.reduce((total, category) => {
        return total + Math.abs(calculatePeriodTotal(category, month));
      }, 0);
      const actualDays = getPeriodDays(month);
      return totalRevenue !== 0 ? (avgAR / totalRevenue) * actualDays : 0;
    }

    // Yearly view with quarterly backend data: Use 4 quarters
    if (viewType === 'yearly' && periodType === 'quarterly') {
      const monthsArr: string[] = getMonthsInPeriod(month);
      const avgAR = arRole
        ? monthsArr.reduce((sum, m: string) => sum + getNWCRoleTotal(arRole, m), 0) / monthsArr.length
        : 0;
      const totalRevenue = revenueCategories.reduce((total, category) => {
        return total + Math.abs(calculatePeriodTotal(category, month));
      }, 0);
      const actualDays = getPeriodDays(month);
      return totalRevenue !== 0 ? (avgAR / totalRevenue) * actualDays : 0;
    }

    // Yearly view with annual backend data: Use single year
    if (viewType === 'yearly' && periodType === 'annual') {
      const accountsReceivable = arRole ? getNWCRoleTotal(arRole, month) : 0;
      const revenue = revenueCategories.reduce((total, category) => {
        return total + Math.abs(calculateMonthlyTotal(category, month));
      }, 0);
      const actualDays = getPeriodDays(month);
      return revenue !== 0 ? (accountsReceivable / revenue) * actualDays : 0;
    }

    return 0;
  };

  const calculateDPO = (month: string) => {
    const apRole = defaultNWCRoleMappings.find(mapping => mapping.role === 'accounts-payable');

    // Find COGS categories from template
    const cogsCategories = activeTemplate
      ? activeTemplate.categories
          .filter(cat => cat.type === 'cogs' || cat.type === 'contra-cogs')
          .map(cat => cat.value)
      : ['cost-of-goods-sold'];

    const periodType = selectedProject?.period_type || 'monthly';

    // Monthly view: Use single period (month/quarter/year) - no averaging
    if (viewType === 'monthly') {
      const accountsPayable = apRole ? getNWCRoleTotal(apRole, month) : 0;
      const cogs = cogsCategories.reduce((total, category) => {
        return total + Math.abs(calculateMonthlyTotal(category, month));
      }, 0);
      const daysInMonth = getDaysInMonth(month);
      return cogs !== 0 ? (accountsPayable / cogs) * daysInMonth : 0;
    }

    // Quarterly view with monthly backend data: Use L3MA-style (3 months)
    if (viewType === 'quarterly' && periodType === 'monthly') {
      const monthsArr: string[] = getMonthsInPeriod(month);
      const avgAP = apRole
        ? monthsArr.reduce((sum, m: string) => sum + getNWCRoleTotal(apRole, m), 0) / monthsArr.length
        : 0;
      const totalCOGS = cogsCategories.reduce((total, category) => {
        return total + Math.abs(calculatePeriodTotal(category, month));
      }, 0);
      const actualDays = getPeriodDays(month);
      return totalCOGS !== 0 ? (avgAP / totalCOGS) * actualDays : 0;
    }

    // Quarterly view with quarterly backend data: Use LQA-style (spot values)
    if (viewType === 'quarterly' && periodType === 'quarterly') {
      const accountsPayable = apRole ? getNWCRoleTotal(apRole, month) : 0;
      const cogs = cogsCategories.reduce((total, category) => {
        return total + Math.abs(calculateMonthlyTotal(category, month));
      }, 0);
      const actualDays = getPeriodDays(month);
      return cogs !== 0 ? (accountsPayable / cogs) * actualDays : 0;
    }

    // Yearly view with monthly backend data: Use LTM-style (12 months)
    if (viewType === 'yearly' && periodType === 'monthly') {
      const monthsArr: string[] = getMonthsInPeriod(month);
      const avgAP = apRole
        ? monthsArr.reduce((sum, m: string) => sum + getNWCRoleTotal(apRole, m), 0) / monthsArr.length
        : 0;
      const totalCOGS = cogsCategories.reduce((total, category) => {
        return total + Math.abs(calculatePeriodTotal(category, month));
      }, 0);
      const actualDays = getPeriodDays(month);
      return totalCOGS !== 0 ? (avgAP / totalCOGS) * actualDays : 0;
    }

    // Yearly view with quarterly backend data: Use 4 quarters
    if (viewType === 'yearly' && periodType === 'quarterly') {
      const monthsArr: string[] = getMonthsInPeriod(month);
      const avgAP = apRole
        ? monthsArr.reduce((sum, m: string) => sum + getNWCRoleTotal(apRole, m), 0) / monthsArr.length
        : 0;
      const totalCOGS = cogsCategories.reduce((total, category) => {
        return total + Math.abs(calculatePeriodTotal(category, month));
      }, 0);
      const actualDays = getPeriodDays(month);
      return totalCOGS !== 0 ? (avgAP / totalCOGS) * actualDays : 0;
    }

    // Yearly view with annual backend data: Use single year
    if (viewType === 'yearly' && periodType === 'annual') {
      const accountsPayable = apRole ? getNWCRoleTotal(apRole, month) : 0;
      const cogs = cogsCategories.reduce((total, category) => {
        return total + Math.abs(calculateMonthlyTotal(category, month));
      }, 0);
      const actualDays = getPeriodDays(month);
      return cogs !== 0 ? (accountsPayable / cogs) * actualDays : 0;
    }

    return 0;
  };

  // Set all categories expanded by default on data load
  useEffect(() => {
    if (!currentDataSource) return;
    const newExpanded: Record<string, boolean> = {};
    currentDataSource.line_items.forEach((item: any) => {
      if (item.type === 'category_header') {
        newExpanded[item.category_value] = true;
      }
    });
    setExpanded(newExpanded);
  }, [currentDataSource]);

  // Auto-select appropriate view based on project period type
  React.useEffect(() => {
    if (selectedProject?.period_type) {
      const periodType = selectedProject.period_type;
      const availableViews: ViewType[] =
        periodType === 'quarterly' ? ['quarterly', 'yearly'] :
        periodType === 'annual' ? ['yearly'] :
        ['monthly', 'quarterly', 'yearly'];

      // If current view is not available for this period type, switch to first available
      if (!availableViews.includes(viewType)) {
        setViewType(availableViews[0]);
      }
    }
  }, [selectedProject?.period_type]);

  // Utility to get tag and row color based on nwc_role
  const getNWCRoleTag = (role: string | undefined) => {
    if (!role || typeof role !== 'string') return null;
    if (['cash', 'short-term-debt', 'debt-like'].includes(role)) {
      return { color: 'bg-amber-100 text-amber-800', label: role.replace(/-/g, ' '), row: 'bg-amber-50' };
    }
    if (['accounts-receivable', 'inventory', 'accounts-payable'].includes(role)) {
      return { color: 'bg-blue-100 text-blue-800', label: role.replace(/-/g, ' '), row: 'bg-blue-50' };
    }
    return null;
  };

  // In transformNWCDetailDataForView, update Net Working Capital % of Net Revenue calculation
  const transformNWCDetailDataForView = (data: any): any => {
    if (!data || !data.periods || !data.line_items) return data;
    if (viewType === 'monthly') return data;
    // Determine period-end months
    const allMonths: string[] = data.periods;
    let targetPeriods: string[] = [];
    if (viewType === 'quarterly') {
      targetPeriods = allMonths.filter((m: string) => ['Mar', 'Jun', 'Sep', 'Dec'].includes(m.split('-')[0]));
      if (!targetPeriods.includes(allMonths[allMonths.length - 1])) {
        targetPeriods.push(allMonths[allMonths.length - 1]);
      }
    } else if (viewType === 'yearly') {
      targetPeriods = allMonths.filter((m: string) => m.split('-')[0] === 'Dec');
      if (!targetPeriods.includes(allMonths[allMonths.length - 1])) {
        targetPeriods.push(allMonths[allMonths.length - 1]);
      }
    }
    // Helper to get months in a period
    const getMonthsInPeriod = (endMonth: string): string[] => {
      if (viewType === 'monthly') return [endMonth];
      const [endMonthStr, yearStr] = endMonth.split('-');
      const year = parseInt(yearStr);
      const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(endMonthStr);
      let startMonthIndex;
      if (viewType === 'quarterly') {
        startMonthIndex = monthIndex - (monthIndex % 3);
      } else { // yearly
        startMonthIndex = 0;
      }
      return allMonths.filter((month: string) => {
        const [monthStr, monthYearStr] = month.split('-');
        const monthYear = parseInt(monthYearStr);
        const monthIdx = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(monthStr);
        if (monthYear !== year) return false;
        return monthIdx >= startMonthIndex && monthIdx <= monthIndex;
      });
    };
    // Transform line items
    const transformedLineItems = data.line_items.map((item: any) => {
      if (!item.values || item.type === 'section_header') {
        return item;
      }
      // For Net Working Capital % of Net Revenue, aggregate using local sum logic
      if (item.name === 'Net Working Capital % of Net Revenue') {
        return {
          ...item,
          values: targetPeriods.map((periodEnd: string) => {
            const monthsInPeriod: string[] = getMonthsInPeriod(periodEnd);
            // Find NWC After Definitional Adjustments for each month in period
            const nwcVals = data.line_items.find((li: any) => li.name === 'Net Working Capital After Definitional Adjustments')?.values || [];
            // Map periodEnd to index in data.periods
            const periodIdxs = monthsInPeriod.map((m: string) => data.periods.indexOf(m)).filter((i: number) => i !== -1);
            const avgNWC = periodIdxs.length > 0 ? periodIdxs.reduce((sum: number, i: number) => sum + (nwcVals[i] || 0), 0) / periodIdxs.length : 0;
            const sumRevenue = getNetRevenueTotal(monthsInPeriod); // Use local sum, not backend
            const totalDays = getPeriodDays(periodEnd);
            if (sumRevenue === 0) return 0;
            return (avgNWC / sumRevenue) * totalDays / 365 * 100;
          })
        };
      }
      // For CCC metrics (DIO/DSO/DPO/CCC), recalculate using the new logic
      if (['Days Inventory Outstanding (DIO)', 'Days Sales Outstanding (DSO)', 'Days Payable Outstanding (DPO)', 'Cash Conversion Cycle'].includes(item.name)) {
        return {
          ...item,
          values: targetPeriods.map((periodEnd: string) => {
            if (item.name === 'Days Inventory Outstanding (DIO)') {
              return calculateDIO(periodEnd);
            } else if (item.name === 'Days Sales Outstanding (DSO)') {
              return calculateDSO(periodEnd);
            } else if (item.name === 'Days Payable Outstanding (DPO)') {
              return calculateDPO(periodEnd);
            } else if (item.name === 'Cash Conversion Cycle') {
              const dio = calculateDIO(periodEnd);
              const dso = calculateDSO(periodEnd);
              const dpo = calculateDPO(periodEnd);
              return dio + dso - dpo;
            }
            return 0;
          })
        };
      }
      // For all other rows, just use the period-end value
      return {
        ...item,
        values: targetPeriods.map((period: string) => {
          const idx = data.periods.indexOf(period);
          return idx !== -1 && item.values ? (item.values[idx] || 0) : 0;
        })
      };
    });
    return { periods: targetPeriods, line_items: transformedLineItems };
  };

  // Helper to get last N months from the end of allMonths
  const getLastNMonths = (n: number): string[] => {
    if (allMonths.length === 0) return [];
    return allMonths.slice(-n);
  };

  // Determine which extra columns to show based on period_type
  const getExtraColumns = (): Array<{key: string, label: string, months: number}> => {
    const periodType = selectedProject?.period_type || 'monthly';

    if (periodType === 'monthly') {
      return [
        { key: 'l3m', label: 'Last 3 Months Avg', months: 3 },
        { key: 'l6m', label: 'Last 6 Months Avg', months: 6 },
        { key: 'ltm', label: 'Last 12 Months', months: 12 }
      ];
    } else if (periodType === 'quarterly') {
      return [
        { key: 'l6m', label: 'Last 6 Months Avg', months: 6 },
        { key: 'ltm', label: 'Last 12 Months', months: 12 }
      ];
    }

    return []; // annual - no extra columns
  };

  const extraColumns = getExtraColumns();

  // Calculate extra column values for a line item
  const calculateExtraColumnValue = (item: any, months: number): number => {
    const lastNMonths = getLastNMonths(months);
    if (lastNMonths.length === 0) return 0;

    // For CCC metrics (DIO/DSO/DPO/CCC), average the calculated metrics - use monthly view calculations
    if (['Days Inventory Outstanding (DIO)', 'Days Sales Outstanding (DSO)', 'Days Payable Outstanding (DPO)', 'Cash Conversion Cycle'].includes(item.name)) {
      let sum = 0;
      lastNMonths.forEach(month => {
        // For these calculations, we want to use monthly view logic
        const inventoryRole = defaultNWCRoleMappings.find(mapping => mapping.role === 'inventory');
        const arRole = defaultNWCRoleMappings.find(mapping => mapping.role === 'accounts-receivable');
        const apRole = defaultNWCRoleMappings.find(mapping => mapping.role === 'accounts-payable');
        const cogsCategories = activeTemplate ? activeTemplate.categories.filter(cat => cat.type === 'cogs' || cat.type === 'contra-cogs').map(cat => cat.value) : [];
        const revenueCategories = activeTemplate ? activeTemplate.categories.filter(cat => cat.type === 'revenue' || cat.type === 'contra-revenue').map(cat => cat.value) : [];

        if (item.name === 'Days Inventory Outstanding (DIO)') {
          const inventory = inventoryRole ? getNWCRoleTotal(inventoryRole, month) : 0;
          const cogs = cogsCategories.reduce((total, category) => total + Math.abs(calculateMonthlyTotal(category, month)), 0);
          const days = getDaysInMonth(month);
          sum += cogs !== 0 ? (inventory / cogs) * days : 0;
        } else if (item.name === 'Days Sales Outstanding (DSO)') {
          const ar = arRole ? getNWCRoleTotal(arRole, month) : 0;
          const revenue = revenueCategories.reduce((total, category) => total + Math.abs(calculateMonthlyTotal(category, month)), 0);
          const days = getDaysInMonth(month);
          sum += revenue !== 0 ? (ar / revenue) * days : 0;
        } else if (item.name === 'Days Payable Outstanding (DPO)') {
          const ap = apRole ? getNWCRoleTotal(apRole, month) : 0;
          const cogs = cogsCategories.reduce((total, category) => total + Math.abs(calculateMonthlyTotal(category, month)), 0);
          const days = getDaysInMonth(month);
          sum += cogs !== 0 ? (ap / cogs) * days : 0;
        } else if (item.name === 'Cash Conversion Cycle') {
          const inventory = inventoryRole ? getNWCRoleTotal(inventoryRole, month) : 0;
          const ar = arRole ? getNWCRoleTotal(arRole, month) : 0;
          const ap = apRole ? getNWCRoleTotal(apRole, month) : 0;
          const cogs = cogsCategories.reduce((total, category) => total + Math.abs(calculateMonthlyTotal(category, month)), 0);
          const revenue = revenueCategories.reduce((total, category) => total + Math.abs(calculateMonthlyTotal(category, month)), 0);
          const days = getDaysInMonth(month);
          const dio = cogs !== 0 ? (inventory / cogs) * days : 0;
          const dso = revenue !== 0 ? (ar / revenue) * days : 0;
          const dpo = cogs !== 0 ? (ap / cogs) * days : 0;
          sum += (dio + dso - dpo);
        }
      });
      return sum / lastNMonths.length;
    }

    // For Net Working Capital % of Net Revenue, sum revenue and recalculate
    if (item.name === 'Net Working Capital % of Net Revenue') {
      // Average the NWC values
      const avgNWC = lastNMonths.reduce((sum, month) => {
        return sum + calculateAdjustedWorkingCapital(month);
      }, 0) / lastNMonths.length;

      // Sum the revenue
      const totalRevenue = getNetRevenueTotal(lastNMonths);

      if (totalRevenue === 0) return 0;

      // Calculate total days in the period
      const totalDays = lastNMonths.reduce((sum, month) => sum + getDaysInMonth(month), 0);

      return (avgNWC / totalRevenue) * totalDays / 365 * 100;
    }

    // For all other balance sheet items, we need to recalculate from raw data
    // Handle specific row types
    if (item.type === 'category_header' || item.type === 'category_total') {
      // Average category totals over the last N months
      const category = item.category_value || item.category;
      if (category) {
        return lastNMonths.reduce((sum, month) => sum + calculateMonthlyTotal(category, month), 0) / lastNMonths.length;
      }
    }

    if (item.type === 'section_total') {
      // Calculate section totals
      if (item.name === 'Total Current Assets') {
        return lastNMonths.reduce((sum, month) => sum + calculateSectionTotal(['current-asset', 'non-current-asset'], month), 0) / lastNMonths.length;
      }
      if (item.name === 'Total Current Liabilities') {
        return lastNMonths.reduce((sum, month) => sum + calculateSectionTotal(['current-liability', 'non-current-liability'], month), 0) / lastNMonths.length;
      }
    }

    if (item.type === 'calculated_total') {
      if (item.name === 'Working Capital') {
        return lastNMonths.reduce((sum, month) => sum + calculateNetWorkingCapital(month), 0) / lastNMonths.length;
      }
      if (item.name === 'Net Working Capital After Definitional Adjustments') {
        return lastNMonths.reduce((sum, month) => sum + calculateAdjustedWorkingCapital(month), 0) / lastNMonths.length;
      }
    }

    if (item.type === 'adjustment') {
      // Handle adjustment rows
      if (item.name.includes('Cash')) {
        const cashRole = defaultNWCRoleMappings.find(m => m.role === 'cash');
        return lastNMonths.reduce((sum, month) => sum + (cashRole ? getNWCRoleTotal(cashRole, month) : 0), 0) / lastNMonths.length;
      }
      if (item.name.includes('Short-term Debt')) {
        const debtRole = defaultNWCRoleMappings.find(m => m.role === 'short-term-debt');
        return lastNMonths.reduce((sum, month) => sum + (debtRole ? getNWCRoleTotal(debtRole, month) : 0), 0) / lastNMonths.length;
      }
    }

    if (item.type === 'account') {
      // For individual accounts, recalculate from raw account data (like category totals do)
      // This ensures we average the underlying monthly data, not just displayed period values
      const accountName = item.name;
      const accountNumber = item.account_number;
      const categoryValue = item.category;

      return lastNMonths.reduce((sum, month) => {
        // Find all accounts matching this account for the month
        // NOTE: Raw accounts use 'accountNumber' (camelCase), not 'account_number'
        const monthAccounts = accounts.filter(a =>
          a.month === month &&
          a.category === categoryValue &&
          a.name === accountName &&
          a.accountNumber === accountNumber
        );

        const categoryType = getCategoryType(categoryValue);
        const isAsset = ['current-asset', 'non-current-asset'].includes(categoryType);
        const isContraAsset = ['contra-current-asset', 'contra-non-current-asset'].includes(categoryType);

        const accountTotal = monthAccounts.reduce((total, account) => {
          return total + ((isAsset || isContraAsset) ? account.debit - account.credit : account.credit - account.debit);
        }, 0);

        return sum + accountTotal;
      }, 0) / lastNMonths.length;
    }

    return 0;
  };

  // Apply view transformations to current data source
  const transformedNwcDetail = React.useMemo(() => {
    return transformNWCDetailDataForView(currentDataSource);
  }, [currentDataSource, viewType]);

  if (!activeTemplate) {
    return (
      <div className="p-8 text-center">
        <p className="text-lg text-gray-600">
          Please select an active template to view the detailed net working capital.
        </p>
      </div>
    );
  }

  if (!transformedNwcDetail) {
    return (
      <div className="p-8 text-center">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">No Net Working Capital detail data available.</p>
        </div>
      </div>
    );
  }

  // Helper to render a row based on type
  const renderRow = (item: any, idx: number) => {
    const tag = getNWCRoleTag(item.nwc_role);
    const rowClass = tag ? tag.row : '';
    switch (item.type) {
      case 'section_header':
        return (
          <tr key={idx} className="bg-gray-50">
            <td className="sticky left-0 z-[20] bg-gray-50 px-6 py-3 text-sm font-semibold text-gray-900 border-r border-gray-200">{item.name}</td>
            <td colSpan={transformedNwcDetail.periods.length + (extraColumns.length > 0 ? 1 : 0) + extraColumns.length + 1} className="px-6 py-3 text-sm font-semibold text-gray-900" />
          </tr>
        );
      case 'category_header':
        return (
          <tr key={idx} className={`cursor-pointer hover:bg-opacity-80 ${rowClass || 'bg-gray-50'}`} onClick={() => setExpanded(e => ({ ...e, [item.category_value]: !e[item.category_value] }))}>
            <td className={`sticky left-0 z-[20] px-6 py-4 text-sm text-gray-900 border-r border-gray-200 ${rowClass || 'bg-gray-50'}`}>
              <div className="flex items-center">
                {expanded[item.category_value] ? <ChevronDown className="h-4 w-4 mr-2" /> : <ChevronRight className="h-4 w-4 mr-2" />}
                <span>{item.name}</span>
                {tag && <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${tag.color}`}>{tag.label}</span>}
              </div>
            </td>
            <td className={`px-6 py-4 text-sm text-gray-900 ${rowClass}`}></td>
            {item.values && item.values.map((val: number, i: number) => (
              <td key={transformedNwcDetail.periods[i]} className={`px-6 py-4 text-sm text-gray-900 text-right ${rowClass}`}>
                {val.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
              </td>
            ))}
            {extraColumns.length > 0 && (
              <td className={`spacer-column ${rowClass}`}></td>
            )}
            {extraColumns.map(col => (
              <td key={col.key} className={`px-6 py-4 text-sm text-gray-900 text-center ${rowClass}`}>
                {calculateExtraColumnValue(item, col.months).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
              </td>
            ))}
          </tr>
        );
      case 'account':
        if (!expanded[item.category]) return null;
        const accountTag = getNWCRoleTag(item.nwc_role);
        const accountRowClass = accountTag ? accountTag.row : '';
        return (
          <tr key={idx} className={`bg-gray-50 ${accountRowClass}`}>
            <td className={`sticky left-0 z-[20] pl-12 py-2 text-sm text-gray-600 border-r border-gray-200 bg-gray-50 ${accountRowClass}`}>{item.name}</td>
            <td className={`px-6 py-2 text-sm text-gray-600 ${accountRowClass}`}>{item.account_number}</td>
            {item.values.map((val: number, i: number) => (
              <td key={i} className={`px-6 py-2 text-sm text-gray-600 text-right ${accountRowClass}`}>{val.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
            ))}
            {extraColumns.length > 0 && (
              <td className={`spacer-column ${accountRowClass}`}></td>
            )}
            {extraColumns.map(col => (
              <td key={col.key} className={`px-6 py-2 text-sm text-gray-600 text-center ${accountRowClass}`}>
                {calculateExtraColumnValue(item, col.months).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
              </td>
            ))}
          </tr>
        );
      case 'category_total':
        if (!expanded[item.category]) return null;
        const totalTag = getNWCRoleTag(item.nwc_role);
        const totalRowClass = totalTag ? totalTag.row : '';
        return (
          <tr key={idx} className={`bg-gray-100 ${totalRowClass}`}>
            <td className={`sticky left-0 z-[20] px-6 py-2 text-sm font-medium text-gray-900 border-r border-gray-200 bg-gray-100 ${totalRowClass}`}>{item.name}</td>
            <td className={`px-6 py-2 text-sm font-medium text-gray-900 ${totalRowClass}`}></td>
            {item.values.map((val: number, i: number) => (
              <td key={i} className={`px-6 py-2 text-sm font-medium text-gray-900 text-right ${totalRowClass}`}>{val.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
            ))}
            {extraColumns.length > 0 && (
              <td className={`spacer-column ${totalRowClass}`}></td>
            )}
            {extraColumns.map(col => (
              <td key={col.key} className={`px-6 py-2 text-sm font-medium text-gray-900 text-center ${totalRowClass}`}>
                {calculateExtraColumnValue(item, col.months).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
              </td>
            ))}
          </tr>
        );
      case 'section_total':
        // Section totals (Total Current Assets, Total Current Liabilities) - light gray
        return (
          <tr key={idx} className="bg-gray-200">
            <td className="sticky left-0 z-[20] bg-gray-200 px-6 py-3 text-sm font-semibold text-gray-900 border-r border-gray-200">{item.name}</td>
            <td className="px-6 py-3 text-sm font-semibold text-gray-900"></td>
            {item.values.map((val: number, i: number) => (
              <td key={i} className="px-6 py-3 text-sm font-semibold text-gray-900 text-right">{val.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
            ))}
            {extraColumns.length > 0 && (
              <td className="spacer-column bg-gray-200"></td>
            )}
            {extraColumns.map(col => (
              <td key={col.key} className="px-6 py-3 text-sm font-semibold text-gray-900 text-center">
                {calculateExtraColumnValue(item, col.months).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
              </td>
            ))}
          </tr>
        );
      case 'calculated_total':
        // Special case: Working Capital row (dark gray, white text)
        if (item.name === 'Working Capital') {
          return (
            <tr key={idx} className="bg-gray-800 text-white">
              <td className="sticky left-0 z-[20] bg-gray-800 px-6 py-4 text-sm font-semibold">{item.name}</td>
              <td className="px-6 py-4 text-sm font-semibold"></td>
              {item.values.map((val: number, i: number) => (
                <td key={i} className="px-6 py-4 text-sm font-semibold text-right">{val.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
              ))}
              {extraColumns.length > 0 && (
                <td className="spacer-column bg-gray-800"></td>
              )}
              {extraColumns.map(col => (
                <td key={col.key} className="px-6 py-4 text-sm font-semibold text-center">
                  {calculateExtraColumnValue(item, col.months).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                </td>
              ))}
            </tr>
          );
        }
        // Special case: Net Working Capital After Definitional Adjustments row (dark gray, white text)
        if (item.name === 'Net Working Capital After Definitional Adjustments') {
          return (
            <tr key={idx} className="bg-gray-800 text-white">
              <td className="sticky left-0 z-[20] bg-gray-800 px-6 py-4 text-sm font-semibold">{item.name}</td>
              <td className="px-6 py-4 text-sm font-semibold"></td>
              {item.values.map((val: number, i: number) => (
                <td key={i} className="px-6 py-4 text-sm font-semibold text-right">{val.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
              ))}
              {extraColumns.length > 0 && (
                <td className="spacer-column bg-gray-800"></td>
              )}
              {extraColumns.map(col => (
                <td key={col.key} className="px-6 py-4 text-sm font-semibold text-center">
                  {calculateExtraColumnValue(item, col.months).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                </td>
              ))}
            </tr>
          );
        }
        // Metrics rows (DIO, DSO, DPO, CCC) - blue shading, tag, 1 decimal, 'days' suffix, font/weight as old UI
        const isMetric = [
          'Days Inventory Outstanding (DIO)',
          'Days Sales Outstanding (DSO)',
          'Days Payable Outstanding (DPO)',
          'Cash Conversion Cycle'
        ].includes(item.name);
        if (isMetric) {
          // Tag label for DIO/DSO/DPO
          const metricTagMap: Record<string, string> = {
            'Days Inventory Outstanding (DIO)': 'inventory',
            'Days Sales Outstanding (DSO)': 'accounts-receivable',
            'Days Payable Outstanding (DPO)': 'accounts-payable',
          };
          const tagLabel = metricTagMap[item.name];
          const tagColor = tagLabel ? 'bg-blue-100 text-blue-800' : '';
          const isCCC = item.name === 'Cash Conversion Cycle';
          return (
            <tr key={idx} className="bg-blue-50">
              <td className="sticky left-0 z-[20] bg-blue-50 px-6 py-4 text-sm text-gray-900">
                <div>
                  <div className="flex items-center">
                    <span className="font-medium">{item.name}</span>
                    {tagLabel && (
                      <span className={`ml-2 text-xs ${tagColor} px-1.5 py-0.5 rounded`}>{tagLabel}</span>
                    )}
                  </div>
                  {/* Subtext for DIO/DSO/DPO */}
                  {item.name === 'Days Inventory Outstanding (DIO)' && (
                    <span className="text-xs text-gray-500">Inventory / COGS * days</span>
                  )}
                  {item.name === 'Days Sales Outstanding (DSO)' && (
                    <span className="text-xs text-gray-500">AR / Net Revenue * days</span>
                  )}
                  {item.name === 'Days Payable Outstanding (DPO)' && (
                    <span className="text-xs text-gray-500">AP / COGS * days</span>
                  )}
                  {item.name === 'Cash Conversion Cycle' && (
                    <span className="text-xs font-normal text-gray-500">DIO + DSO - DPO</span>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 text-sm text-gray-900 bg-blue-50"></td>
              {item.values.map((val: number, i: number) => (
                <td key={i} className={`px-6 py-4 text-sm text-gray-900 text-right bg-blue-50${isCCC ? ' font-semibold' : ''}`}>
                  {typeof val === 'number' ? val.toLocaleString('en-US', { style: 'decimal', minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' days' : val}
                </td>
              ))}
              {extraColumns.length > 0 && (
                <td className="spacer-column bg-blue-50"></td>
              )}
              {extraColumns.map(col => (
                <td key={col.key} className={`px-6 py-4 text-sm text-gray-900 text-center bg-blue-50${isCCC ? ' font-semibold' : ''}`}>
                  {calculateExtraColumnValue(item, col.months).toLocaleString('en-US', { style: 'decimal', minimumFractionDigits: 1, maximumFractionDigits: 1 })} days
                </td>
              ))}
            </tr>
          );
        }
        // For other calculated_total rows, use default styling
        return (
          <tr key={idx} className="bg-gray-100 font-semibold">
            <td className="sticky left-0 z-[20] bg-gray-100 px-6 py-4 text-sm text-gray-900">
              <span>{item.name}</span>
            </td>
            <td className="px-6 py-4 text-sm text-gray-900"></td>
            {item.values.map((val: number, i: number) => (
              <td key={i} className="px-6 py-4 text-sm text-gray-900 text-right">
                {typeof val === 'number' ? val.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : val}
              </td>
            ))}
            {extraColumns.length > 0 && (
              <td className="spacer-column bg-gray-100"></td>
            )}
            {extraColumns.map(col => (
              <td key={col.key} className="px-6 py-4 text-sm text-gray-900 text-center">
                {calculateExtraColumnValue(item, col.months).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
              </td>
            ))}
          </tr>
        );
      case 'calculated_percentage':
        // Net Working Capital % of Net Revenue row (dark blue, white text)
        return (
          <tr key={idx} className="bg-gray-700 text-white">
            <td className="sticky left-0 z-[20] bg-gray-700 px-6 py-4 text-sm font-semibold">{item.name}</td>
            <td className="px-6 py-4 text-sm font-semibold"></td>
            {item.values.map((val: number, i: number) => (
              <td key={i} className="px-6 py-4 text-sm font-semibold text-right">{typeof val === 'number' ? val.toFixed(1) + '%' : val}</td>
            ))}
            {extraColumns.length > 0 && (
              <td className="spacer-column bg-gray-700"></td>
            )}
            {extraColumns.map(col => (
              <td key={col.key} className="px-6 py-4 text-sm font-semibold text-center">
                {calculateExtraColumnValue(item, col.months).toFixed(1)}%
              </td>
            ))}
          </tr>
        );
      case 'adjustment':
        // Adjustment rows (Less: Cash, Plus: Short-term Debt) - yellow shading, tag, currency formatting
        const adjTag = getNWCRoleTag(item.nwc_role);
        const adjRowClass = adjTag ? adjTag.row : '';
        return (
          <tr key={idx} className={`highlight-row ${adjRowClass}`}>
            <td className={`sticky left-0 z-[20] px-6 py-4 text-sm text-gray-900 border-r border-gray-200 ${adjRowClass}`}>
              <div className="flex items-center">
                <span>{item.name}</span>
                {adjTag && <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${adjTag.color}`}>{adjTag.label}</span>}
              </div>
            </td>
            <td className={`px-6 py-4 text-sm text-gray-900 ${adjRowClass}`}></td>
            {item.values.map((val: number, i: number) => (
              <td key={i} className={`px-6 py-4 text-sm text-gray-900 text-right ${adjRowClass}`}>{val.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
            ))}
            {extraColumns.length > 0 && (
              <td className={`spacer-column ${adjRowClass}`}></td>
            )}
            {extraColumns.map(col => (
              <td key={col.key} className={`px-6 py-4 text-sm text-gray-900 text-center ${adjRowClass}`}>
                {calculateExtraColumnValue(item, col.months).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
              </td>
            ))}
          </tr>
        );
      default:
        return null;
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Add custom styles for table layout */}
      <style>
        {`
          .financial-table {
            table-layout: auto;
          }

          .financial-table th:first-child,
          .financial-table td:first-child {
            width: 240px;
            min-width: 240px;
            max-width: 240px;
          }

          /* Account number column */
          .financial-table th:nth-child(2) {
            width: auto;
            min-width: 120px;
            max-width: 140px;
            white-space: normal;
            word-break: break-word;
            text-align: center;
          }
          .financial-table td:nth-child(2) {
            width: auto;
            min-width: 120px;
            max-width: 140px;
            white-space: nowrap;
            text-align: center;
          }

          .financial-table th:not(:first-child):not(:nth-child(2)),
          .financial-table td:not(:first-child):not(:nth-child(2)) {
            width: auto;
            min-width: 160px;
            max-width: 200px;
          }

          .financial-table th.value-column {
            width: auto;
            min-width: 160px;
            max-width: 200px;
          }

          .financial-table th.spacer-column,
          .financial-table td.spacer-column {
            width: 2px;
            min-width: 2px;
            max-width: 2px;
            padding: 0;
            background-color: #f9fafb !important;
          }

          .highlight-row {
            background-color: #fffbeb;
          }
        `}
      </style>

      {/* Sticky controls */}
      <div className="sticky top-0 z-50 border-b border-gray-200 mb-4">
        <div className="flex justify-end items-center space-x-4 p-4">
          <ViewToggle
            currentView={viewType}
            onViewChange={setViewType}
            projectPeriodType={selectedProject?.period_type}
          />

          
          <button
            onClick={expandAll}
            className={`inline-flex items-center px-3 py-2 border text-sm leading-4 font-medium rounded-md transition-colors ${
              // Check if all categories are expanded (similar to Income Statement logic)
              currentDataSource && currentDataSource.line_items.filter(item => item.type === 'category_header' && item.category_value).every(item => expanded[item.category_value!])
                ? 'text-white bg-blue-600 border-transparent'
                : 'text-blue-700 bg-blue-100 border-blue-300 hover:bg-blue-200'
            }`}
          >
            <ChevronDownSquare className="h-4 w-4 mr-2" />
            Expand All
          </button>
          <button
            onClick={collapseAll}
            className={`inline-flex items-center px-3 py-2 border text-sm leading-4 font-medium rounded-md transition-colors ${
              // Check if all categories are collapsed (similar to Income Statement logic)
              currentDataSource && !currentDataSource.line_items.filter(item => item.type === 'category_header' && item.category_value).some(item => expanded[item.category_value!])
                ? 'text-white bg-blue-600 border-transparent'
                : 'text-blue-700 bg-blue-100 border-blue-300 hover:bg-blue-200'
            }`}
          >
            <ChevronUpSquare className="h-4 w-4 mr-2" />
            Collapse All
          </button>
        </div>
      </div>

      {/* Add NWC Role Legend */}
      <NWCRoleLegend />

      {/* Template info */}
      <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center">
        <span className="mr-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
          Active Template:
        </span>
        <span className="text-sm text-blue-800 font-medium">
          {activeTemplate.name}
        </span>
      </div>

      {/* Table container */}
      <div className="overflow-hidden border border-gray-200 rounded-lg">
        <div className="border border-gray-200 rounded-lg">
          <div className="max-h-screen h-128 overflow-auto">
            <div className="inline-block min-w-fit">
              <table className="financial-table divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th className="sticky left-0 top-0 z-[110] px-6 py-3 text-left text-sm font-semibold text-black border-r border-b border-gray-200 bg-gray-50">Description</th>
                    <th className="sticky top-0 z-[100] px-6 py-3 text-left text-sm font-semibold text-black border-b border-gray-200 bg-gray-50">Account Number</th>
                    {months.map(month => (
                      <th key={month} className="sticky top-0 z-[100] px-6 py-3 text-right text-sm font-semibold text-black border-b border-gray-200 value-column bg-gray-50">
                        {getDateForPeriod(month, accounts)}
                      </th>
                    ))}
                    {extraColumns.length > 0 && (
                      <th className="sticky top-0 z-[100] px-2 py-3 border-b border-gray-200 bg-gray-50 spacer-column"></th>
                    )}
                    {extraColumns.map(col => (
                      <th key={col.key} className="sticky top-0 z-[100] px-6 py-3 text-center text-sm font-semibold text-black border-b border-gray-200 value-column bg-gray-50">
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {transformedNwcDetail.line_items.map((item: any, idx: number) => renderRow(item, idx))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

// Add display name for debugging
UnifiedDetailedNetWorkingCapital.displayName = 'UnifiedDetailedNetWorkingCapital';

export default UnifiedDetailedNetWorkingCapital;